/**
 * ======================================================================================================================
 * CRO CART — Vegana Natura
 * ----------------------------------------------------------------------------------------------------------------------
 * <free-shipping-progress> custom element.
 *
 * Unlike the theme's stock <free-shipping-bar>, this element does NOT multiply the threshold by the currency rate.
 * The threshold is passed already expressed in the customer's presentment currency (resolved per country in Liquid),
 * which lets us ship flat, country-specific values (e.g. CH 120 / AT 120 / DE 60) instead of FX-converted numbers.
 *
 * Attributes
 *   threshold     : minimum amount (in presentment-currency cents) required for free shipping
 *   total-price   : current qualifying cart total (in presentment-currency cents)
 *   data-reached  : HTML message shown once the threshold is met
 *   data-unreached: HTML message shown below the threshold ({{ ... }} placeholder is replaced by the remaining amount)
 * ======================================================================================================================
 */

(function () {
  if (window.customElements.get('free-shipping-progress')) return;

  class FreeShippingProgress extends HTMLElement {
    static get observedAttributes() {
      return ['threshold', 'total-price'];
    }

    constructor() {
      super();
      this._onCartChanged = this._onCartChanged.bind(this);

      try {
        this._formatter = new Intl.NumberFormat(window.Shopify && Shopify.locale, {
          style: 'currency',
          currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CHF',
        });
      } catch (e) {
        this._formatter = null;
      }
    }

    connectedCallback() {
      this._messageEl = this.querySelector('[data-cro-message]');
      this._fillEl = this.querySelector('[data-cro-fill]');
      document.addEventListener('cart:change', this._onCartChanged);
      this._update();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onCartChanged);
    }

    attributeChangedCallback() {
      if (this.isConnected) this._update();
    }

    get threshold() {
      return parseFloat(this.getAttribute('threshold')) || 0;
    }

    get totalPrice() {
      return parseFloat(this.getAttribute('total-price')) || 0;
    }

    set totalPrice(value) {
      this.setAttribute('total-price', value);
    }

    _formatMoney(cents) {
      if (this._formatter) return this._formatter.format(cents / 100);
      return (cents / 100).toFixed(2);
    }

    _update() {
      const threshold = this.threshold;
      const total = this.totalPrice;
      const reached = threshold > 0 && total >= threshold;
      const progress = threshold > 0 ? Math.min(total / threshold, 1) : 1;

      this.classList.toggle('is-reached', reached);

      if (this._fillEl) {
        this._fillEl.style.setProperty('--cro-progress', progress.toFixed(4));
      }

      if (this._messageEl) {
        if (reached) {
          this._messageEl.innerHTML = this.getAttribute('data-reached') || '';
        } else {
          const remaining = this._formatMoney(threshold - total);
          const amount = `<span class="cro-amount">${remaining}</span>`;
          const template = this.getAttribute('data-unreached') || '';
          this._messageEl.innerHTML = template.replace(/\{\{.*?\}\}/g, amount);
        }
      }
    }

    _onCartChanged(event) {
      const cart = event && event.detail && event.detail.cart;
      if (!cart) return;

      const qualifying = (cart.items || [])
        .filter((item) => item.requires_shipping)
        .reduce((sum, item) => sum + item.final_line_price, 0);

      const cartDiscount = (cart.cart_level_discount_applications || []).reduce(
        (sum, application) => sum + (application.total_allocated_amount || 0),
        0
      );

      this.totalPrice = Math.max(qualifying - cartDiscount, 0);
    }
  }

  window.customElements.define('free-shipping-progress', FreeShippingProgress);
})();

/**
 * Hide the Klaviyo floating teaser while the cart drawer is open (and restore it on close),
 * so it doesn't overlap the drawer. Tracks the drawer's `open` attribute directly (robust in both
 * directions) and mirrors it onto a body class the stylesheet keys off.
 */
(function () {
  if (window.__croCartDrawerTeaser) return;
  window.__croCartDrawerTeaser = true;

  function init() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    var sync = function () {
      document.body.classList.toggle('cart-drawer-open', drawer.hasAttribute('open'));
    };

    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * Buy-box review prominence: the store's rating comes from the Klaviyo Reviews app block, which shows stars + a
 * review count but no numeric score. We inject the bold average score (parsed from the widget's aria-label) in
 * front of the stars — the single strongest trust signal — and let cro.css handle the premium styling.
 * Klaviyo loads asynchronously, so we re-check for a short window after load.
 */
(function () {
  if (window.__croKlaviyoScore) return;
  window.__croKlaviyoScore = true;

  function qualifier(value) {
    if (value >= 4.5) return 'Ausgezeichnet · ';
    if (value >= 4.0) return 'Sehr gut · ';
    if (value >= 3.0) return 'Gut · ';
    return '';
  }

  function enhance() {
    var widgets = document.querySelectorAll('.product-info .kl_reviews__star_rating_widget');

    for (var i = 0; i < widgets.length; i++) {
      var btn = widgets[i];
      if (btn.querySelector('.cro-kl-score')) continue;

      var label = btn.getAttribute('aria-label') || '';
      var match = label.replace(/ /g, ' ').match(/^\s*([\d]+(?:[.,]\d+)?)/);
      if (!match) continue;

      var badge = btn.querySelector('.kl_reviews__stars_badge');
      if (!badge) continue;

      var value = parseFloat(match[1].replace(',', '.'));

      var score = document.createElement('span');
      score.className = 'cro-kl-score';
      score.setAttribute('aria-hidden', 'true');
      score.textContent = match[1].replace(',', '.');
      badge.parentNode.insertBefore(score, badge);

      // Prefix the review count with a trust qualifier ("Ausgezeichnet" etc.), rendered via CSS ::before.
      var labelEl = btn.querySelector('.kl_reviews__star_rating_widget__label');
      if (labelEl) labelEl.setAttribute('data-cro-qual', qualifier(value));
    }
  }

  function boot() {
    enhance();
    var tries = 0;
    var interval = setInterval(function () {
      enhance();
      if (++tries > 20) clearInterval(interval);
    }, 400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

/**
 * Store review badge — live store-wide aggregate from Klaviyo Reviews.
 * The cart trust badge (snippets/cart-trust.liquid) is server-rendered with the static fallback values from theme
 * settings, so it's instant and works without JS. When "Auto-update from Klaviyo Reviews" is enabled, we fetch the
 * store-wide summary (average score + verified review count) straight from Klaviyo's public onsite reviews API — the
 * same endpoint their widgets use — and update the score, star fill and count in place. The result is cached in
 * sessionStorage (so we hit the network at most once per session) and re-applied whenever the cart re-renders.
 */
(function () {
  if (window.__croStoreRating) return;
  window.__croStoreRating = true;

  var CACHE_KEY = 'croStoreRating';
  var TTL = 30 * 60 * 1000; // 30 minutes
  var summary = null; // { rating: Number, count: Number }

  function badges() {
    // Matches the cart trust badge and any other opted-in placement (e.g. the homepage rating block).
    return document.querySelectorAll('[data-cro-review-badge]');
  }

  function formatCount(n) {
    try {
      return new Intl.NumberFormat(window.Shopify && Shopify.locale).format(n);
    } catch (e) {
      return String(n);
    }
  }

  function apply() {
    if (!summary) return;
    var list = badges();
    for (var i = 0; i < list.length; i++) {
      var badge = list[i];
      var scale = parseFloat(badge.getAttribute('data-scale')) || 5;

      var scoreEl = badge.querySelector('[data-cro-score]');
      if (scoreEl) scoreEl.textContent = summary.rating.toFixed(1);

      var fill = badge.querySelector('.cro-stars__layer--fill');
      if (fill) {
        var pct = Math.max(0, Math.min(100, (summary.rating / scale) * 100));
        fill.style.setProperty('--cro-fill', pct + '%');
      }

      var countEl = badge.querySelector('[data-cro-count]');
      if (countEl) countEl.textContent = formatCount(summary.count);
    }
  }

  function readCache() {
    try {
      var data = JSON.parse(sessionStorage.getItem(CACHE_KEY));
      if (!data || Date.now() - data.t > TTL) return null;
      return { rating: data.rating, count: data.count };
    } catch (e) {
      return null;
    }
  }

  function writeCache() {
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ rating: summary.rating, count: summary.count, t: Date.now() })
      );
    } catch (e) {
      /* private mode / storage full — just skip caching */
    }
  }

  function fetchSummary(company) {
    var url =
      'https://fast.a.klaviyo.com/reviews/api/client_reviews/all/?product_id=all&company_id=' +
      encodeURIComponent(company) +
      '&limit=1&offset=0&sort=3&filter=&type=reviews&media=false&subtype=all';

    return fetch(url, { headers: { accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (json) {
        var s = json && json.summary;
        if (!s || typeof s.star_rating !== 'number' || typeof s.review_count !== 'number') return null;
        return { rating: s.star_rating, count: s.review_count };
      })
      .catch(function () {
        return null;
      });
  }

  function boot() {
    var first = badges()[0];
    if (!first) return;

    var company = first.getAttribute('data-kl-company');
    if (!company) return;

    summary = readCache();
    if (summary) {
      apply();
      return;
    }

    fetchSummary(company).then(function (result) {
      if (!result) return; // keep the server-rendered fallback
      summary = result;
      writeCache();
      apply();
    });
  }

  // The theme replaces the badge markup when the cart re-renders — re-apply the cached values afterwards.
  document.addEventListener('cart:change', function () {
    setTimeout(apply, 60);
    setTimeout(apply, 400);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
